import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Sparkles, Loader2, Lock, Zap, Target } from "lucide-react";
import { Link } from "react-router-dom";
import StartTrialButton from "@/components/StartTrialButton";

export default function AIFitAnalysis({ grantId }) {
  const { user } = useAuth();
  const canUse = ["pro", "enterprise"].includes(user?.subscription_tier);
  const hasProfile = user?.org_profile && (user.org_profile.focus_areas?.length || user.org_profile.mission);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setBusy(true);
    try {
      const res = await api.post("/ai/matching/analyze", { grant_id: grantId });
      setResult(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Analysis failed");
    } finally {
      setBusy(false);
    }
  };

  if (!canUse) {
    return (
      <div className="bg-white border border-[#E6E4DF] rounded-lg p-6" data-testid="ai-fit-locked">
        <div className="flex items-start gap-3">
          <Target className="w-5 h-5 text-[#5E5E5E]"/>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="font-heading text-lg font-medium">AI Fit Analysis</h3>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#C1654B] text-white font-bold uppercase tracking-widest">Pro</span>
            </div>
            <p className="text-sm text-[#5E5E5E] mt-1">
              Get Claude Sonnet 4.5's honest read on this grant — mission fit, competitive positioning, and a go/no-go recommendation.
            </p>
            {user && !user.has_used_trial ? (
              <div className="mt-4 flex flex-col gap-2 items-start">
                <StartTrialButton testId="ai-fit-start-trial" />
                <Link to="/pricing" className="text-xs text-[#5E5E5E] hover:text-[#1C3F35] underline">or see all plans</Link>
              </div>
            ) : (
              <Link to="/pricing">
                <Button data-testid="ai-fit-upgrade" className="mt-4 bg-[#1C3F35] text-white hover:bg-[#1A362D]" size="sm">
                  <Lock className="w-3 h-3 mr-2"/>Upgrade to Pro
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!hasProfile) {
    return (
      <div className="bg-white border border-[#E6E4DF] rounded-lg p-6" data-testid="ai-fit-no-profile">
        <div className="flex items-start gap-3">
          <Target className="w-5 h-5 text-[#1C3F35]"/>
          <div className="flex-1">
            <h3 className="font-heading text-lg font-medium">AI Fit Analysis</h3>
            <p className="text-sm text-[#5E5E5E] mt-1">
              Add your organization profile in Settings first so Claude has context.
            </p>
            <Link to="/settings">
              <Button data-testid="ai-fit-add-profile" className="mt-4 bg-[#1C3F35] text-white hover:bg-[#1A362D]" size="sm">Add profile</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-[#E6E4DF] rounded-lg p-6" data-testid="ai-fit-panel">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-md bg-[#1C3F35] flex items-center justify-center shrink-0">
            <Sparkles className="w-4 h-4 text-white"/>
          </div>
          <div>
            <h3 className="font-heading text-lg font-medium">AI Fit Analysis</h3>
            <p className="text-xs text-[#5E5E5E] mt-1">Claude Sonnet 4.5 · scored against your org profile</p>
          </div>
        </div>
        {!result && (
          <Button
            onClick={run} disabled={busy}
            data-testid="ai-fit-analyze"
            className="bg-[#1C3F35] text-white hover:bg-[#1A362D]" size="sm"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <><Zap className="w-3 h-3 mr-2"/>Analyze fit</>}
          </Button>
        )}
      </div>

      {result && (
        <div className="mt-6" data-testid="ai-fit-result">
          <div className="flex items-baseline gap-3 mb-4">
            <div className="font-heading text-5xl font-light text-[#1C3F35]" data-testid="ai-fit-score">{result.score}<span className="text-2xl text-[#5E5E5E]">%</span></div>
            <div className="text-sm text-[#5E5E5E]">fit</div>
            {result.cached && <span className="ml-auto text-[10px] uppercase tracking-widest text-[#7A8C77] font-bold">Cached</span>}
          </div>
          <div className="text-sm text-[#1A1A1A] leading-relaxed whitespace-pre-wrap font-mono bg-[#F9F8F6] border border-[#E6E4DF] rounded p-4">
            {result.analysis}
          </div>
          <Button
            onClick={()=>{setResult(null); run();}}
            disabled={busy}
            variant="outline" size="sm"
            data-testid="ai-fit-rerun"
            className="mt-3 border-[#E6E4DF]"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin"/> : "Re-analyze"}
          </Button>
        </div>
      )}
    </div>
  );
}
